<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { sourceColor } from 'dashboard/components-next/Attribution/attributionUtils';

// "Origen de los leads": una barra por fuente UTM con su porcentaje sobre el
// total y, debajo, las ventas y la conversión de esa fuente. Los tres datos
// ya venían en la respuesta del informe (utm_totals, utm_revenue y el
// recuento de la etapa ganada); antes sólo se veían dentro de la matriz.
const props = defineProps({
  sources: { type: Array, default: () => [] },
  totals: { type: Object, default: () => ({}) },
  wonCounts: { type: Object, default: () => ({}) },
});

const { t, locale } = useI18n();
const key = 'OVERVIEW_REPORTS.LEAD_SOURCES';

const numberFormatter = computed(
  () => new Intl.NumberFormat(locale.value || 'es')
);

const rows = computed(() => {
  const totals = props.totals || {};
  const grandTotal = props.sources.reduce(
    (sum, source) => sum + (Number(totals[source]) || 0),
    0
  );

  return props.sources
    .map(source => {
      const total = Number(totals[source]) || 0;
      const won = Number(props.wonCounts?.[source]) || 0;
      return {
        source,
        total,
        won,
        share: grandTotal ? Math.round((total / grandTotal) * 100) : 0,
        conversion: total ? Math.round((won / total) * 1000) / 10 : null,
        color: sourceColor(source),
      };
    })
    .filter(row => row.total > 0)
    .sort((a, b) => b.total - a.total);
});
</script>

<template>
  <section
    class="flex flex-col gap-3.5 px-4 py-3.5 border rounded-xl border-n-weak bg-n-solid-2"
  >
    <h5 class="mb-0 text-base font-semibold text-n-slate-12">
      {{ t(`${key}.HEADER`) }}
    </h5>

    <div v-if="rows.length" class="flex flex-col gap-3">
      <div v-for="row in rows" :key="row.source" class="flex flex-col gap-1">
        <div class="flex items-baseline gap-2">
          <span class="flex-1 text-[13px] truncate text-n-slate-12">
            {{ row.source }}
          </span>
          <span class="text-[13px] font-semibold tabular-nums text-n-slate-12">
            {{ numberFormatter.format(row.total) }}
          </span>
          <span class="text-xs tabular-nums text-n-slate-10 w-9 text-right">
            {{ row.share }}%
          </span>
        </div>
        <div class="h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full rounded-full"
            :style="{
              width: `${row.share}%`,
              backgroundColor: row.color,
            }"
          />
        </div>
        <span class="text-[11px] text-n-slate-10">
          <template v-if="row.conversion === null">
            {{ t(`${key}.NO_DATA`) }}
          </template>
          <template v-else>
            {{ t(`${key}.WON`, { count: numberFormatter.format(row.won) }) }}
            ·
            {{ t(`${key}.CONVERSION`, { rate: row.conversion }) }}
          </template>
        </span>
      </div>
    </div>

    <p v-else class="mb-0 text-sm text-n-slate-10">
      {{ t(`${key}.EMPTY`) }}
    </p>
  </section>
</template>
